import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import PractitionerProfile from "@/pages/PractitionerProfile";
import Learn from "@/pages/Learn";
import LearnEntry from "@/pages/LearnEntry";
import Corner from "@/pages/Corner";
import CornerPost from "@/pages/CornerPost";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ConfirmEmail from "@/pages/ConfirmEmail";
import EmailPreferences from "@/pages/EmailPreferences";
import RedeemGift from "@/pages/RedeemGift";
import AuthCallback from "@/pages/AuthCallback";
import PractitionerDashboard from "@/pages/PractitionerDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import MyBookings from "@/pages/MyBookings";
import MyRipples from "@/pages/MyRipples";
import Contact from "@/pages/Contact";
import PractitionerFAQ from "@/pages/PractitionerFAQ";
import SeekerFAQ from "@/pages/SeekerFAQ";
import Policies from "@/pages/Policies";
import GetTheApp from "@/pages/GetTheApp";
import Welcome from "@/pages/Welcome";
import Membership from "@/pages/Membership";
import Community from "@/pages/Community";
import Featured from "@/pages/Featured";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

const Protected = ({ role, children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/practitioner/:id" element={<PractitionerProfile />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/learn/:slug" element={<LearnEntry />} />
        <Route path="/corner" element={<Corner />} />
        <Route path="/corner/:slug" element={<CornerPost />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/redeem/:code" element={<RedeemGift />} />
        <Route path="/email-preferences" element={<Protected><EmailPreferences /></Protected>} />
        <Route path="/bookings" element={<Protected><MyBookings /></Protected>} />
        <Route path="/ripples" element={<Protected><MyRipples /></Protected>} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/how-joining-works" element={<PractitionerFAQ />} />
        <Route path="/how-booking-works" element={<SeekerFAQ />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/get-the-app" element={<GetTheApp />} />
        <Route path="/welcome" element={<Protected><Welcome /></Protected>} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/community" element={<Community />} />
        <Route path="/featured" element={<Featured />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/dashboard" element={<Protected role="practitioner"><PractitionerDashboard /></Protected>} />
        <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
